import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { StageStepper } from './StageBadge'
import { TaskStatus } from './TaskStatus'
import { AuthoringReview } from './AuthoringReview'
import { Icon, type IconName } from './Icon'
import { stageDef } from '@/lib/stages'
import { useAuth } from '@/auth/AuthContext'
import type { Role, TaskDetail } from '@/lib/types'

export type TaskSection = 'monitor' | 'work' | 'brief' | 'rubric' | 'coach' | 'files' | 'activity'
const shared: { id: TaskSection; label: string; icon: IconName }[] = [
  { id: 'work', label: 'Work', icon: 'grid' }, { id: 'brief', label: 'Brief', icon: 'book' },
  { id: 'rubric', label: 'Rubric', icon: 'tasks' }, { id: 'coach', label: 'Coach', icon: 'chat' }, { id: 'files', label: 'Files', icon: 'file' },
  { id: 'activity', label: 'Activity', icon: 'clock' },
]

const MONITOR = { id: 'monitor' as TaskSection, label: 'Monitor', icon: 'zap' as IconName }

/**
 * Both portals share this navigation, so Monitor has to be added per viewer rather than to
 * the shared list: it is developer-facing, and an expert must not see an empty tab. It
 * appears from the build stage on, which is both where the agent starts working and the
 * first stage at which the expert's exports are guaranteed to exist.
 *
 * The build stage has no Work tab. Nothing there is the developer's to do -- the agent
 * authors the package and advances the task itself -- so Monitor is the whole page. The
 * exception is a task with no authoring job at all: those predate the agent and still need
 * the manual build form.
 */
export function sectionsFor(role: Role, stage: number, automated = true) {
  if (role !== 'engineer' || stage < 3) return shared
  if (stage === 3 && automated) return [MONITOR, ...shared.filter(s => s.id !== 'work')]
  return [MONITOR, ...shared]
}
const SectionContext = createContext<TaskSection>('work')

/** Panels stay mounted: navigation must not reset edits or interrupt uploads. */
export function TaskPanel({ section, children }: { section: TaskSection | TaskSection[]; children: ReactNode }) {
  const current = useContext(SectionContext)
  return <div hidden={Array.isArray(section) ? !section.includes(current) : section !== current} className="task-panel">{children}</div>
}

export function TaskSectionLink({ section, children, review = false, className = 'btn' }: { section: TaskSection; children: ReactNode; review?: boolean; className?: string }) {
  const [params] = useSearchParams()
  const next = new URLSearchParams(params)
  next.set('section', section)
  if (review) next.set('review', '1')
  return <Link className={className} aria-current={params.get('section') === section || (!params.get('section') && section === 'work') ? 'page' : undefined} to={`?${next}`}>{children}</Link>
}

export function TaskWorkspace({ data, role, actions, children }: { data: TaskDetail; role: Role; actions: ReactNode; children: ReactNode }) {
  const { user } = useAuth()
  const [params] = useSearchParams()
  const [actionsOpen, setActionsOpen] = useState(() => !window.matchMedia('(max-width: 1000px)').matches)
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1000px)')
    const change = () => setActionsOpen(!media.matches)
    media.addEventListener('change', change)
    return () => media.removeEventListener('change', change)
  }, [])
  const sections = sectionsFor(role, Number(data.task.stage), !!data.task.authoring)
  const requested = params.get('section')
  // Landing on a tab this stage does not have (a stale link, or the build stage losing
  // Work) must not render an empty page: fall back to the first tab that exists.
  const current = sections.find(s => s.id === requested)?.id
    ?? (sections.some(s => s.id === 'work') ? 'work' : sections[0].id)
  const { task, refinement } = data
  const stage = stageDef(task.stage)
  const owner = task.active_review_id ? 'sme' : stage.owner
  const assignedElsewhere = role === 'engineer' && owner === 'engineer' && !!task.engineer_id && task.engineer_id !== user?.id
  const unassigned = owner === 'engineer' && !task.engineer_id
  const waiting = (owner !== role || assignedElsewhere) && owner !== null
  const withWhom = owner === 'sme' ? 'expert' : owner === 'agent' ? 'authoring agent' : 'developer'
  // The review hand-off lives on whichever tab this viewer actually has.
  const reviewSection: TaskSection = sections.some(s => s.id === 'work') ? 'work' : 'monitor'
  return <SectionContext.Provider value={current}>
    <div className="task-progress"><StageStepper stage={task.stage} viewer={role} /></div>
    <AuthoringReview data={data} role={role} />
    <div className="task-context">
      <div><div className="eyebrow">{stage.label}</div><h2>{owner === null ? 'Task complete' : unassigned ? 'Needs a developer' : assignedElsewhere ? 'With the assigned developer' : waiting ? `With the ${withWhom}` : 'Your turn'}</h2><TaskStatus task={task} role={role} /></div>
      {refinement?.can_request_review && <TaskSectionLink section={reviewSection} review className="btn review-cta"><Icon name="mail" />Request expert review</TaskSectionLink>}
      {refinement?.can_respond && <TaskSectionLink section={reviewSection} review className="btn primary">Review & return<Icon name="arrow" /></TaskSectionLink>}
    </div>
    <nav className="task-tabs" aria-label="Task sections">{sections.map(s => <TaskSectionLink key={s.id} section={s.id} className={`task-tab ${current === s.id ? 'active' : ''}`}><Icon name={s.icon} size={16} />{s.label}{s.id === 'files' && <span className="tab-count">{data.files.length}</span>}</TaskSectionLink>)}</nav>
    <div className="workspace-columns">
      <div className="workspace-content" key={task.task_id}>{children}</div>
      <aside className="action-rail" aria-label="Task actions">
        <details className="action-details" open={actionsOpen} onToggle={e => setActionsOpen(e.currentTarget.open)}><summary>Next steps</summary>
          {actions}
          {refinement?.can_respond && <div className="rail-message"><Icon name="mail" /><h3>Your review is requested</h3><p>Read the results, refine the rubric, and return your feedback.</p><TaskSectionLink section={reviewSection} review>Review & return</TaskSectionLink></div>}
          {data.actions.length === 0 && !refinement?.can_respond && <div className="rail-message"><Icon name={owner === null ? 'check' : 'clock'} /><h3>{owner === null ? 'All done' : 'No action needed'}</h3><p>{owner === null ? 'The brief, files, and review history are available here whenever you need them.' : 'You can browse the task materials while the next step is being worked on.'}</p></div>}
          <div className="rail-links"><span className="eyebrow">Task materials</span><TaskSectionLink section="brief" className="rail-link">Read the brief<Icon name="arrow" size={15} /></TaskSectionLink><TaskSectionLink section="rubric" className="rail-link">Rubric & examples<Icon name="arrow" size={15} /></TaskSectionLink><TaskSectionLink section="files" className="rail-link">Browse files<Icon name="arrow" size={15} /></TaskSectionLink></div>
        </details>
      </aside>
    </div>
  </SectionContext.Provider>
}
